import { BloodGlucoseObservationWithReading } from "@/model"
import { textForFormContext } from "@/model/BloodGlucoseFormContext"
import { DateTimeFormatter } from "@js-joda/core"
import { Locale } from "@js-joda/locale"
import { ChatBubbleIcon, LockClosedIcon } from "@radix-ui/react-icons"
import {
  needsFeedback,
  readingValueBackgroundColorFor,
  readingValueBorderColorFor,
  readingValueTextColorFor,
  textForClassification,
} from "../dashboardData"

type ReadingCardProps = {
  observation: BloodGlucoseObservationWithReading
  onClick?: (observation: BloodGlucoseObservationWithReading) => void
}

const TIME_FORMAT = DateTimeFormatter.ofPattern("h:mm a").withLocale(Locale.US)

const ReadingCard = ({ observation, onClick }: ReadingCardProps) => {
  const { reading, form } = observation
  const label = textForClassification(reading.classification)
  const feedback = needsFeedback(observation)
  const formContext = form ? `${textForFormContext(form.context)}, ` : ""
  const style = feedback
    ? { backgroundColor: "#FEF9C3", borderColor: "#FFE21A" }
    : {
        backgroundColor: readingValueBackgroundColorFor(reading.classification),
        borderColor: readingValueBorderColorFor(reading.classification),
      }

  return (
    <button
      type="button"
      onClick={() => onClick?.(observation)}
      className="flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left"
      style={style}
    >
      <div className="flex flex-col gap-1">
        <span className="text-sm text-primary-contrast">Glucose</span>
        <span className="flex items-baseline gap-1">
          <span className="text-[40px] font-bold leading-none text-primary-contrast">
            {reading.bloodGlucoseInMilligramsPerDeciliter}
          </span>
          <span className="text-sm text-primary-contrast">mg/dL</span>
        </span>
      </div>

      <div className="flex flex-col items-end gap-2">
        <span className="text-sm text-primary-contrast">
          {formContext}
          {observation.dateTime.format(TIME_FORMAT)}
        </span>
        <span className="flex items-center gap-2">
          {!observation.isFormEditable && (
            <LockClosedIcon aria-label="Locked reading" className="h-4 w-4" />
          )}
          {feedback ? (
            <span className="flex items-center gap-1 rounded-md bg-[rgba(255,223,0,0.5)] px-2.5 py-1 text-sm italic text-primary-contrast">
              <ChatBubbleIcon aria-hidden className="h-3.5 w-3.5" />
              Needs feedback
            </span>
          ) : label ? (
            <span
              className="rounded-md px-2 py-1 text-sm font-semibold"
              style={{
                backgroundColor: readingValueBackgroundColorFor(reading.classification),
                color: readingValueTextColorFor(reading.classification),
              }}
            >
              {label}
            </span>
          ) : null}
        </span>
      </div>
    </button>
  )
}

export default ReadingCard
